import type { FilterCategory, FilterSection, FilterKey } from "./types";

/**
 * Filter category definitions shared across components.
 * Used by FiltersSidebar and CompaniesExplorePage.
 */
export const FILTER_CATEGORIES: FilterCategory[] = [
  {
    id: "intent",
    label: "Intent",
    section: "INTENT",
    enabled: true,
  },
  {
    id: "fitScore",
    label: "Fit Score",
    section: "SCORING",
    enabled: true,
  },
  {
    id: "intentScore",
    label: "Intent Score",
    section: "SCORING",
    enabled: true,
  },
  {
    id: "modelScore",
    label: "Fit/Need/Intent",
    section: "SCORING",
    enabled: true,
  },
  {
    id: "companyType",
    label: "Company Type",
    section: "FIRMOGRAPHICS",
    enabled: true,
  },
  {
    id: "employees",
    label: "Employee Count",
    section: "FIRMOGRAPHICS",
    enabled: true,
  },
  { id: "revenue", label: "Revenue", section: "FIRMOGRAPHICS", enabled: true },
  {
    id: "industry",
    label: "Industry",
    section: "FIRMOGRAPHICS",
    enabled: true,
  },
  {
    id: "geography",
    label: "Geography",
    section: "FIRMOGRAPHICS",
    enabled: true,
  },
  {
    id: "globalPresence",
    label: "Global Presence",
    section: "FIRMOGRAPHICS",
    enabled: true,
  },
  {
    id: "rankings",
    label: "Fortune 500 / Forbes 2000",
    section: "FIRMOGRAPHICS",
    enabled: true,
  },
  {
    id: "promotionDate",
    label: "Company Promotion Date",
    section: "FIRMOGRAPHICS",
    enabled: true,
    superKuduOnly: true,
  },
  {
    id: "productCategories",
    label: "Product Categories",
    section: "TECHNOGRAPHICS",
    enabled: true,
  },
  {
    id: "products",
    label: "Products",
    section: "TECHNOGRAPHICS",
    enabled: true,
  },
  { id: "vendors", label: "Vendors", section: "TECHNOGRAPHICS", enabled: true },
  {
    id: "productAttributes",
    label: "Product Attributes",
    section: "TECHNOGRAPHICS",
    enabled: true,
  },
  { id: "itSpend", label: "IT Spend", section: "IT_SPEND", enabled: true },
  { id: "aiSpend", label: "AI Spend", section: "AI_SPEND", enabled: true },
  // gated by universeJsonExport — filtered in the Companies explore pages
  {
    id: "contactMode",
    label: "Filter Mode",
    section: "CONTACTS",
    enabled: true,
  },
  {
    id: "contactJobSeniority",
    label: "Job Seniority",
    section: "CONTACTS",
    enabled: true,
  },
  {
    id: "contactJobFunction",
    label: "Job Function",
    section: "CONTACTS",
    enabled: true,
  },
  {
    id: "contactJobTitle",
    label: "Job Title",
    section: "CONTACTS",
    enabled: true,
  },
  {
    id: "contactLocation",
    label: "Contact Location",
    section: "CONTACTS",
    enabled: true,
  },
  {
    id: "contactTimeInRole",
    label: "Time in Current Role",
    section: "CONTACTS",
    enabled: true,
  },
  // gated by universe-json-export FF — filtered in CompaniesExplorePage
  {
    id: "buyerIntentProducts",
    label: "Products",
    section: "BUYER_INTENT",
    enabled: true,
  },
  {
    id: "buyerIntentVendors",
    label: "Vendors",
    section: "BUYER_INTENT",
    enabled: true,
  },
  {
    id: "buyerIntentCategories",
    label: "Categories",
    section: "BUYER_INTENT",
    enabled: true,
  },
  {
    id: "buyerIntentAttributes",
    label: "Attributes",
    section: "BUYER_INTENT",
    enabled: true,
  },
  // gated by universe-json-export FF — filtered in CompaniesExplorePage
  {
    id: "companyConfig",
    label: "Company Configuration",
    section: "COMPANY_CONFIG",
    enabled: true,
  },
  // gated by universeJsonExport — filtered in the Companies explore pages
  {
    id: "financingEvents",
    label: "Financing Events",
    section: "EVENTS",
    enabled: true,
  },
];

/**
 * Filter titles indexed by filter key.
 * Derived from FILTER_CATEGORIES for quick lookup.
 */
export const FILTER_TITLES: Record<FilterKey, string> = {
  ...FILTER_CATEGORIES.reduce(
    (acc, cat) => {
      acc[cat.id] = cat.label;
      return acc;
    },
    {} as Record<FilterKey, string>
  ),
  targetOrganizations: "Search Companies",
  uploadedAccounts: "Uploaded Accounts",
  contactMode: "Filter Mode",
  contactJobSeniority: "Job Seniority",
  contactJobFunction: "Job Function",
  contactJobTitle: "Job Title",
  contactLocation: "Contact Location",
  contactTimeInRole: "Time in Current Role",
  buyerIntentProducts: "Products",
  buyerIntentVendors: "Vendors",
  buyerIntentCategories: "Categories",
  buyerIntentAttributes: "Attributes",
  knownCompanies: "Known Companies",
};

/**
 * Section labels for display in the sidebar.
 */
export const SECTION_LABELS: Record<FilterSection, string> = {
  ACCOUNTS: "ACCOUNTS",
  INTENT: "INTENT",
  SCORING: "SCORING",
  FIRMOGRAPHICS: "FIRMOGRAPHICS",
  TECHNOGRAPHICS: "TECHNOGRAPHICS",
  IT_SPEND: "IT SPEND",
  AI_SPEND: "AI SPEND",
  CONTACTS: "CONTACTS [UNIVERSE ONLY]",
  BUYER_INTENT: "BUYER INTENT [UNIVERSE ONLY]",
  COMPANY_CONFIG: "COMPANY CONFIGURATION [UNIVERSE ONLY]",
  EVENTS: "EVENTS [UNIVERSE ONLY]",
};
